import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { lancamentosService } from '../services/lancamentosService';
import { anoLetivoService } from '../services/anoLetivoService';
import styles from './RelatoriosPage.module.css';
import { FileText, Download, Send } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import RelatorioPDF from '../components/RelatorioPDF';
import { useAuth } from '../contexts/AuthContext';
import * as ExcelJS from 'exceljs';
import turmasService from '../services/turmasService';
import type {
  AnoLetivo,
  RelatorioFinanceiro,
  RelatorioInadimplentes,
} from '../types/relatorios';

export default function RelatoriosPage() {
  const { user } = useAuth();
  const isOperador = user?.tipo === 'operador';
  const [relatorio, setRelatorio] = useState<RelatorioFinanceiro | null>(null);
  const [inadimplentes, setInadimplentes] = useState<RelatorioInadimplentes | null>(null);
  const [anosLetivos, setAnosLetivos] = useState<AnoLetivo[]>([]);
  const [selectedAnoLetivo, setSelectedAnoLetivo] = useState<number | ''>('');
  const [tipoRelatorio, setTipoRelatorio] = useState<'completo' | 'financeiro' | 'inadimplentes'>(
    isOperador ? 'inadimplentes' : 'completo'
  );
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);

  const showFinanceiro = !isOperador && (tipoRelatorio === 'completo' || tipoRelatorio === 'financeiro');
  const showInadimplentes = tipoRelatorio === 'completo' || tipoRelatorio === 'inadimplentes';

  useEffect(() => {
    loadAnosLetivos();
  }, []);

  useEffect(() => {
    loadRelatorio();
  }, [selectedAnoLetivo]);

  const loadAnosLetivos = async () => {
    try {
      const data = await anoLetivoService.listarAnosLetivos();
      setAnosLetivos(data);
      // Seleciona o ano ativo por padrão
      const anoAtivo = data.find((a: AnoLetivo) => a.ativo);
      if (anoAtivo) {
        setSelectedAnoLetivo(anoAtivo.id);
      }
    } catch (error) {
      console.error('Erro ao carregar anos letivos:', error);
    }
  };

  const loadRelatorio = async () => {
    setLoading(true);
    try {
      const anoId = selectedAnoLetivo || undefined;
      const [dataFinanceiro, dataInadimplentes] = await Promise.all([
        lancamentosService.getRelatorioFinanceiro(anoId as number),
        lancamentosService.getRelatorioInadimplentes(anoId as number),
      ]);
      setRelatorio(dataFinanceiro);
      setInadimplentes(dataInadimplentes);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const getInadimplenciaClass = (value: number) => {
    if (value <= 5) return 'low';
    if (value <= 15) return 'medium';
    return 'high';
  };

  const handleExportToExcel = async () => {
    setExportingExcel(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'SAGEX';
      workbook.created = new Date();

      const turmas = await turmasService.findAll();

      if (!turmas || turmas.length === 0) {
        alert('Nenhuma turma encontrada para exportar.');
        setExportingExcel(false);
        return;
      }

      const dadosTurmas = await Promise.all(
        turmas.map(async (turma: any) => {
          try {
            const alunosLancas = await lancamentosService.getByTurma(turma.id);
            return { turma, alunosLancamentos: alunosLancas || [] };
          } catch (error) {
            console.error(`Erro ao carregar dados da turma ${turma.nome}:`, error);
            return { turma, alunosLancamentos: [] };
          }
        })
      );

      // --- ABA RESUMO ---
      const resumoSheet = workbook.addWorksheet('Resumo');
      resumoSheet.columns = [
        { header: 'Nº Chamada', key: 'nChamada', width: 15 },
        { header: 'Nome do Aluno', key: 'nome', width: 35 },
        { header: 'Valor Mensalidade', key: 'valor', width: 20 },
        { header: 'Responsável', key: 'responsavel', width: 35 },
        { header: 'Precisa Tirar NF?', key: 'tirarNf', width: 20 },
        { header: 'Série', key: 'serie', width: 25 },
        { header: 'Valor Total em Débito', key: 'debito', width: 25 }
      ];

      resumoSheet.getRow(1).font = { bold: true };

      // Dados para o resumo
      dadosTurmas.forEach(({ turma, alunosLancamentos }) => {

        // Criar aba para cada turma
        let sheetName = turma.nome.substring(0, 31).replace(/[:\\/?*[\]]/g, '');
        const existingNames = workbook.worksheets.map(w => w.name);
        let finalSheetName = sheetName;
        let counter = 1;
        while (existingNames.includes(finalSheetName)) {
          finalSheetName = `${sheetName.substring(0, 28)}_${counter}`;
          counter++;
        }

        const ws = workbook.addWorksheet(finalSheetName);

        if (!alunosLancamentos || alunosLancamentos.length === 0) {
          ws.addRow(['Nenhum aluno encontrado']);
          return;
        }

        const pAluno = alunosLancamentos.find((a: any) => a.lancamentos && a.lancamentos.length > 0);
        const descricoesParcelas = pAluno ? pAluno.lancamentos.map((l: any) => l.descricao) || [] : [];

        const headers = ['Aluno', 'Responsável'];
        descricoesParcelas.forEach((desc: string) => {
          headers.push(`${desc} - Valor Final`, `${desc} - Status`, `${desc} - N.º NFSe`);
        });

        const headerRow = ws.addRow(headers);
        headerRow.font = { bold: true };

        // Ajustar largura colunas
        ws.getColumn(1).width = 35;
        ws.getColumn(2).width = 35;
        for (let i = 0; i < descricoesParcelas.length; i++) {
          const baseCol = 3 + (i * 3);
          ws.getColumn(baseCol).width = 15;
          ws.getColumn(baseCol + 1).width = 15;
          ws.getColumn(baseCol + 2).width = 15;
        }

        // Adicionar Alunos
        let nChamada = 1;
        alunosLancamentos.forEach((alunoData: any) => {
          let debitoTotal = 0;
          let valorMensal = 0;

          const rowData: any[] = [
            alunoData.alunoNome || '',
            alunoData.responsavelNome || 'Não informado',
          ];

          if (alunoData.lancamentos) {
            alunoData.lancamentos.forEach((l: any) => {
              rowData.push(
                Number(l.valorFinal).toFixed(2).replace('.', ','),
                l.status,
                l.numeroNFSE || ''
              );

              if (l.status === 'vencida' || l.status === 'atrasada' || l.status === 'atrasado') {
                debitoTotal += Number(l.valorFinal);
              }
              if (l.descricao?.toLowerCase().includes('mensalidade')) {
                valorMensal = Number(l.valorFinal);
              }
            });
          }

          const row = ws.addRow(rowData);

          // Aplicar background vermelho se estiver ATRASADO
          if (alunoData.lancamentos) {
            alunoData.lancamentos.forEach((l: any, idx: number) => {
              const statusStr = (l.status || '').toLowerCase();
              const wasAtrasadoObj = statusStr === 'vencida' || statusStr === 'atrasada' || statusStr === 'atrasado';
              if (wasAtrasadoObj) {
                const cellCol = 3 + (idx * 3) + 1; // Coluna do Status
                const cell = row.getCell(cellCol);
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FFFFCCCC' } // Vermelho claro
                };
                cell.font = { color: { argb: 'FF990000' } };

                // Pintar tbm a celula do valor
                const valCell = row.getCell(cellCol - 1);
                valCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCCCC' } };
                valCell.font = { color: { argb: 'FF990000' } };
              }
            });
          }

          // Preencher Aba Resumo
          const tirarNfStr = alunoData.lancamentos?.[0]?.contrato?.emitirNfse !== false ? 'S' : 'N';

          resumoSheet.addRow({
            nChamada: nChamada++,
            nome: alunoData.alunoNome || '',
            valor: valorMensal > 0 ? valorMensal.toFixed(2).replace('.', ',') : '',
            responsavel: alunoData.responsavelNome || 'Não informado',
            tirarNf: tirarNfStr,
            serie: turma.nome,
            debito: debitoTotal.toFixed(2).replace('.', ',')
          });
        });
      });

      // Baixar arquivo gerado usando Blob
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Mensalidades_Completo_${new Date().toISOString().split('T')[0]}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erro ao exportar excel:', error);
      alert('Ocorreu um erro ao exportar o arquivo Excel.');
    } finally {
      setExportingExcel(false);
    }
  };



  return (
    <Layout title={isOperador ? "Relatório de Inadimplentes" : "Relatórios Financeiros"}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isOperador ? 'Relatório de Inadimplentes' : 'Relatório Financeiro Mensal'}</h2>
          <div className={styles.headerActions}>
            <div className={styles.filters}>
              <span className={styles.filterLabel}>Ano Letivo:</span>
              <select
                className={styles.filterSelect}
                value={selectedAnoLetivo}
                onChange={(e) => setSelectedAnoLetivo(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todos os anos</option>
                {anosLetivos.map((ano) => (
                  <option key={ano.id} value={ano.id}>
                    {ano.ano} {ano.ativo ? '(Ativo)' : ''}
                  </option>
                ))}
              </select>

              <span className={styles.filterLabel} style={{ marginLeft: '12px' }}>Tipo:</span>
              <select
                className={styles.filterSelect}
                value={tipoRelatorio}
                onChange={(e) => setTipoRelatorio(e.target.value as any)}
                disabled={isOperador}
              >
                {!isOperador && <option value="completo">Relatório Completo</option>}
                {!isOperador && <option value="financeiro">Relatório Financeiro</option>}
                <option value="inadimplentes">Relatório de Inadimplentes</option>
              </select>
            </div>

            {!isOperador && (
              <button
                className={styles.exportExcelButton}
                onClick={handleExportToExcel}
                disabled={exportingExcel}
              >
                <Download size={16} />
                {exportingExcel ? 'Gerando...' : 'Exportar Mensalidades Excel'}
              </button>
            )}

            {!isOperador && (
              <button className={styles.enviarDiretoriaBtn} onClick={() => { }}>
                <Send size={16} />
                Enviar para Diretoria
              </button>
            )}
            {relatorio || inadimplentes ? (
              <PDFDownloadLink
                document={
                  <RelatorioPDF
                    relatorio={relatorio}
                    inadimplentes={inadimplentes}
                    anoLetivoId={selectedAnoLetivo === '' ? null : Number(selectedAnoLetivo)}
                    tipo={tipoRelatorio}
                  />
                }
                fileName={`relatorio-${tipoRelatorio}-${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`}
                className={styles.pdfLink}
                key={`${selectedAnoLetivo}-${tipoRelatorio}-${relatorio?.meses.length || 0}-${inadimplentes?.totais.totalCasos ?? 0}`}
              >
                {({ loading }) => (
                  <span className={styles.pdfButton}>
                    <Download size={16} />
                    {loading ? 'Gerando...' : 'Gerar PDF'}
                  </span>
                )}
              </PDFDownloadLink>
            ) : (
              <button className={styles.pdfButton} disabled>
                <Download size={16} />
                Gerar PDF
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Carregando relatório...
          </div>
        ) : (!relatorio || relatorio.meses.length === 0) && (!inadimplentes || inadimplentes.casos.length === 0) ? (
          <div className={styles.empty}>
            <FileText className={styles.emptyIcon} />
            <p className={styles.emptyText}>Nenhum dado encontrado para gerar o relatório.</p>
          </div>
        ) : (
          <>
            {/* Cards de resumo */}
            {showFinanceiro && relatorio && (
              <div className={styles.summaryCards}>
                <div className={`${styles.summaryCard} ${styles.esperado}`}>
                  <div className={styles.cardLabel}>Total Esperado</div>
                  <div className={styles.cardValue}>{formatCurrency(relatorio.totais.esperado)}</div>
                  <div className={styles.cardSubtext}>
                    {relatorio.totais.totalLancamentos} lançamentos
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.recebido}`}>
                  <div className={styles.cardLabel}>Total Recebido</div>
                  <div className={styles.cardValue}>{formatCurrency(relatorio.totais.recebido)}</div>
                  <div className={styles.cardSubtext}>
                    {relatorio.totais.lancamentosPagos} pagos
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.faturado}`}>
                  <div className={styles.cardLabel}>Faturado (c/ NF)</div>
                  <div className={styles.cardValue}>{formatCurrency(relatorio.totais.faturado)}</div>
                  <div className={styles.cardSubtext}>
                    {relatorio.totais.lancamentosFaturados} com nota fiscal
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.pendente}`}>
                  <div className={styles.cardLabel}>Pendente</div>
                  <div className={styles.cardValue}>{formatCurrency(relatorio.totais.pendente)}</div>
                  <div className={styles.cardSubtext}>
                    Aguardando pagamento
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.atrasado}`}>
                  <div className={styles.cardLabel}>Em Atraso</div>
                  <div className={styles.cardValue}>{formatCurrency(relatorio.totais.atrasado)}</div>
                  <div className={styles.cardSubtext}>
                    {relatorio.totais.lancamentosAtrasados} atrasados
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.inadimplencia}`}>
                  <div className={styles.cardLabel}>Taxa de Inadimplência</div>
                  <div className={styles.cardValue}>{formatPercent(relatorio.totais.inadimplencia)}</div>
                  <div className={styles.cardSubtext}>
                    Sobre o total esperado
                  </div>
                </div>
              </div>
            )}

            {/* Tabela detalhada por mês */}
            {showFinanceiro && relatorio && (
              <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                  <h3 className={styles.tableTitle}>Detalhamento por Mês</h3>
                </div>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Mês/Ano</th>
                        <th className={styles.textRight}>Esperado</th>
                        <th className={styles.textRight}>Recebido</th>
                        <th className={styles.textRight}>Faturado</th>
                        <th className={styles.textRight}>Pendente</th>
                        <th className={styles.textRight}>Atrasado</th>
                        <th className={styles.textCenter}>Inadimplência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatorio.meses.map((mes) => (
                        <tr key={mes.chave}>
                          <td>
                            <strong>{mes.mes}</strong> {mes.ano}
                          </td>
                          <td className={`${styles.textRight} ${styles.valorEsperado}`}>
                            {formatCurrency(mes.esperado)}
                          </td>
                          <td className={`${styles.textRight} ${styles.valorRecebido}`}>
                            {formatCurrency(mes.recebido)}
                          </td>
                          <td className={`${styles.textRight} ${styles.valorFaturado}`}>
                            {formatCurrency(mes.faturado)}
                          </td>
                          <td className={`${styles.textRight} ${styles.valorPendente}`}>
                            {formatCurrency(mes.pendente)}
                          </td>
                          <td className={`${styles.textRight} ${styles.valorAtrasado}`}>
                            {formatCurrency(mes.atrasado)}
                          </td>
                          <td>
                            <div className={styles.inadimplenciaCell}>
                              <div className={styles.inadimplenciaBar}>
                                <div
                                  className={`${styles.inadimplenciaFill} ${styles[getInadimplenciaClass(mes.inadimplencia)]}`}
                                  style={{ width: `${Math.min(mes.inadimplencia, 100)}%` }}
                                />
                              </div>
                              <span className={`${styles.inadimplenciaValue} ${styles[getInadimplenciaClass(mes.inadimplencia)]}`}>
                                {formatPercent(mes.inadimplencia)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className={styles.tableFooter}>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td className={`${styles.textRight} ${styles.valorEsperado}`}>
                          {formatCurrency(relatorio.totais.esperado)}
                        </td>
                        <td className={`${styles.textRight} ${styles.valorRecebido}`}>
                          {formatCurrency(relatorio.totais.recebido)}
                        </td>
                        <td className={`${styles.textRight} ${styles.valorFaturado}`}>
                          {formatCurrency(relatorio.totais.faturado)}
                        </td>
                        <td className={`${styles.textRight} ${styles.valorPendente}`}>
                          {formatCurrency(relatorio.totais.pendente)}
                        </td>
                        <td className={`${styles.textRight} ${styles.valorAtrasado}`}>
                          {formatCurrency(relatorio.totais.atrasado)}
                        </td>
                        <td>
                          <div className={styles.inadimplenciaCell}>
                            <div className={styles.inadimplenciaBar}>
                              <div
                                className={`${styles.inadimplenciaFill} ${styles[getInadimplenciaClass(relatorio.totais.inadimplencia)]}`}
                                style={{ width: `${Math.min(relatorio.totais.inadimplencia, 100)}%` }}
                              />
                            </div>
                            <span className={`${styles.inadimplenciaValue} ${styles[getInadimplenciaClass(relatorio.totais.inadimplencia)]}`}>
                              {formatPercent(relatorio.totais.inadimplencia)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Gráfico de barras empilhadas */}
            {showFinanceiro && relatorio && (
              <div className={styles.chartsSection}>
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Evolução Mensal</h3>
                  <div className={styles.barChart}>
                    {relatorio.meses.map((mes) => {
                      const total = mes.recebido + mes.pendente + mes.atrasado;
                      const recebidoPct = total > 0 ? (mes.recebido / total) * 100 : 0;
                      const pendentePct = total > 0 ? (mes.pendente / total) * 100 : 0;
                      const atrasadoPct = total > 0 ? (mes.atrasado / total) * 100 : 0;

                      return (
                        <div key={mes.chave} className={styles.barItem}>
                          <span className={styles.barLabel}>
                            {mes.mes.substring(0, 3)}/{mes.ano.toString().slice(-2)}
                          </span>
                          <div className={styles.barContainer}>
                            <div
                              className={`${styles.barSegment} ${styles.recebido}`}
                              style={{ width: `${recebidoPct}%` }}
                              title={`Recebido: ${formatCurrency(mes.recebido)}`}
                            />
                            <div
                              className={`${styles.barSegment} ${styles.pendente}`}
                              style={{ width: `${pendentePct}%` }}
                              title={`Pendente: ${formatCurrency(mes.pendente)}`}
                            />
                            <div
                              className={`${styles.barSegment} ${styles.atrasado}`}
                              style={{ width: `${atrasadoPct}%` }}
                              title={`Atrasado: ${formatCurrency(mes.atrasado)}`}
                            />
                          </div>
                          <span className={styles.barValue}>
                            {formatCurrency(total)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.legend}>
                    <div className={styles.legendItem}>
                      <div className={`${styles.legendColor} ${styles.recebido}`}></div>
                      <span>Recebido</span>
                    </div>
                    <div className={styles.legendItem}>
                      <div className={`${styles.legendColor} ${styles.pendente}`}></div>
                      <span>Pendente</span>
                    </div>
                    <div className={styles.legendItem}>
                      <div className={`${styles.legendColor} ${styles.atrasado}`}></div>
                      <span>Atrasado</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showInadimplentes && inadimplentes && (
              <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                  <h3 className={styles.tableTitle}>Inadimplentes por Responsável/Aluno</h3>
                </div>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Responsável</th>
                        <th>Aluno</th>
                        <th>Turma</th>
                        <th>Meses</th>
                        <th className={styles.textRight}>Total em Atraso</th>
                        <th>Valores por Mês</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(inadimplentes?.resumo || []).map((linha, idx) => (
                        <tr key={`${linha.responsavelId || 'r'}-${linha.alunoId || 'a'}-${idx}`}>
                          <td>{linha.responsavelNome}</td>
                          <td>{linha.alunoNome}</td>
                          <td>{linha.turmaNome || '-'}</td>
                          <td>
                            <div className={styles.chipList}>
                              {linha.meses.map((mes) => (
                                <span key={mes} className={styles.chip}>{mes}</span>
                              ))}
                            </div>
                          </td>
                          <td className={`${styles.textRight} ${styles.valorAtrasado}`}>
                            {formatCurrency(linha.totalAtrasado)}
                          </td>
                          <td>
                            <div className={styles.mesValores}>
                              {linha.valoresPorMes.map((m) => (
                                <div key={m.chave} className={styles.mesValorItem}>
                                  <span className={styles.mesValorLabel}>{m.mes}</span>
                                  <span className={styles.mesValorValue}>{formatCurrency(m.valor)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(inadimplentes?.resumo || []).length === 0 && (
                        <tr>
                          <td colSpan={6} className={styles.textCenter}>
                            Nenhum inadimplente encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {inadimplentes && inadimplentes.resumo.length > 0 && (
                      <tfoot className={styles.tableFooter}>
                        <tr>
                          <td colSpan={4}><strong>Total</strong></td>
                          <td className={`${styles.textRight} ${styles.valorAtrasado}`}>
                            {formatCurrency(inadimplentes.totais.totalAtrasado)}
                          </td>
                          <td>
                            {inadimplentes.totais.totalCasos} casos / {inadimplentes.totais.totalLancamentos} lançamentos
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {showInadimplentes && inadimplentes && (
              <div className={styles.detailSection}>
                <div className={styles.tableHeader}>
                  <h3 className={styles.tableTitle}>Detalhes para Judicialização</h3>
                </div>
                <div className={styles.detailList}>
                  {(inadimplentes?.casos || []).map((caso, idx) => (
                    <div key={`caso-${idx}`} className={styles.detailCard}>
                      <div className={styles.detailHeader}>
                        <div>
                          <div className={styles.detailTitle}>
                            {caso.responsavel?.nome || caso.responsavel?.razaoSocial || 'Responsável não informado'}
                          </div>
                          <div className={styles.detailSubtitle}>
                            {caso.aluno?.nome || 'Aluno não informado'} {caso.turma?.nome ? `• ${caso.turma.nome}` : ''}
                          </div>
                        </div>
                        <div className={styles.detailTotal}>
                          {formatCurrency(caso.totalAtrasado)}
                        </div>
                      </div>

                      <div className={styles.detailGrid}>
                        <div>
                          <div className={styles.fieldLabel}>Responsável</div>
                          <div className={styles.fieldValue}>Nome: {caso.responsavel?.nome || '-'}</div>
                          <div className={styles.fieldValue}>Razão Social: {caso.responsavel?.razaoSocial || '-'}</div>
                          <div className={styles.fieldValue}>CPF/CNPJ: {caso.responsavel?.cpfCnpj || '-'}</div>
                          <div className={styles.fieldValue}>Email: {caso.responsavel?.email || '-'}</div>
                          <div className={styles.fieldValue}>Telefone: {caso.responsavel?.telefone || '-'}</div>
                          <div className={styles.fieldValue}>Tel. Residencial: {caso.responsavel?.telefoneResidencial || '-'}</div>
                          <div className={styles.fieldValue}>Tel. Comercial: {caso.responsavel?.telefoneComercial || '-'}</div>
                          <div className={styles.fieldValue}>
                            Endereço: {caso.responsavel?.endereco?.endereco || '-'} {caso.responsavel?.endereco?.numero || ''}
                          </div>
                          <div className={styles.fieldValue}>
                            Bairro: {caso.responsavel?.endereco?.bairro || '-'} • CEP: {caso.responsavel?.endereco?.cep || '-'}
                          </div>
                          <div className={styles.fieldValue}>
                            Cidade/UF: {caso.responsavel?.endereco?.cidade || '-'} / {caso.responsavel?.endereco?.estado || '-'}
                          </div>
                        </div>

                        <div>
                          <div className={styles.fieldLabel}>Aluno</div>
                          <div className={styles.fieldValue}>Nome: {caso.aluno?.nome || '-'}</div>
                          <div className={styles.fieldValue}>Matrícula: {caso.aluno?.matricula || '-'}</div>
                          <div className={styles.fieldValue}>CPF: {caso.aluno?.cpf || '-'}</div>
                          <div className={styles.fieldValue}>RG: {caso.aluno?.rg || '-'}</div>
                          <div className={styles.fieldValue}>Nascimento: {formatDate(caso.aluno?.dataNascimento)}</div>
                          <div className={styles.fieldValue}>Email: {caso.aluno?.email || '-'}</div>
                          <div className={styles.fieldValue}>Telefone: {caso.aluno?.telefone || '-'}</div>
                          <div className={styles.fieldLabel}>Contrato</div>
                          <div className={styles.fieldValue}>Número: {caso.contrato?.numeroSequencial ?? '-'}</div>
                          <div className={styles.fieldValue}>Dia Vencimento: {caso.contrato?.diaVencimento ?? '-'}</div>
                          <div className={styles.fieldValue}>Valor Mensal: {caso.contrato?.valorMensal != null ? formatCurrency(Number(caso.contrato.valorMensal)) : '-'}</div>
                          <div className={styles.fieldValue}>Desconto: {caso.contrato?.percentualDescontoMensal != null ? `${caso.contrato.percentualDescontoMensal}%` : '-'}</div>
                        </div>
                      </div>

                      <div className={styles.detailSectionTitle}>Meses em atraso</div>
                      <div className={styles.mesValores}>
                        {caso.valoresPorMes.map((m) => (
                          <div key={m.chave} className={styles.mesValorItem}>
                            <span className={styles.mesValorLabel}>{m.mes}</span>
                            <span className={styles.mesValorValue}>{formatCurrency(m.valor)}</span>
                          </div>
                        ))}
                      </div>

                      <div className={styles.detailSectionTitle}>Lançamentos</div>
                      <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              <th>Descrição</th>
                              <th>Vencimento</th>
                              <th className={styles.textRight}>Valor</th>
                              <th>Status</th>
                              <th>Parcela</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caso.lancamentos.map((l) => (
                              <tr key={l.id}>
                                <td>{l.descricao}</td>
                                <td>{formatDate(l.dataVencimento)}</td>
                                <td className={`${styles.textRight} ${styles.valorAtrasado}`}>{formatCurrency(Number(l.valorFinal))}</td>
                                <td>{l.status}</td>
                                <td>{l.numeroParcela}/{l.totalParcelas}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  {(inadimplentes?.casos || []).length === 0 && (
                    <div className={styles.empty}>
                      <p className={styles.emptyText}>Nenhum caso para judicialização.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
